/**
 * useChecklistInstancesList — hooks da listagem central de vistorias.
 *
 * Toda leitura passa pelas RPCs SECURITY DEFINER (padrão tenant v2.1):
 *   - get_checklist_instances_paginated (lista e colunas do kanban)
 *   - get_checklist_instances_kpis      (indicadores do topo)
 * A permissão da página é validada no banco pela chave do `view`
 * ('list' -> vehicles_inspections | 'kanban' -> vehicles_inspections_kanban).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-03 v1.7.0 - Filtro opcional por origem (manual/periódica)
 *            repassado à RPC v1.8.0; drill-down do KPI consolidado de Veículos.
 *   RELEASE: 2026-09-29 v1.6.0 - Realtime passa a chegar (GRANT SELECT authenticated); pendência com
 *            aba oculta + recarga ao voltar (padrão Tarefas); polling de 60 s nas colunas.
 *   RELEASE: 2026-09-27 v1.5.0 - Período por criação/conclusão, ordenação
 *            server-side e responsável configurado completo na Lista.
 *   RELEASE: 2026-09-26 v1.4.0 - Performance: colunas do Kanban sem polling de 60 s
*            (Realtime cuida) e com placeholderData ao trocar filtros.
*   RELEASE: 2026-09-26 v1.3.0 - Tipo de Checklist e filtros opcionais por modo
 *            e responsável específico, compartilhados por lista, KPIs e Kanban.
 *   RELEASE: 2026-09-26 v1.2.0 - Proprietário histórico, avatar do responsável
 *            e filtros sincronizados por cliente/contato.
 *   RELEASE: 2026-09-26 v1.1.0 - Cursor composto por coluna, caches separados,
 *            contagens filtradas, atualização periódica e Realtime granular.
 *   RELEASE: 2026-09-26 v1.0.0 - Versão inicial + realtime por tenant.
 */
import { useEffect } from 'react';
import { InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { resolveInspectionStatus } from '@/components/vehicles/inspections/kanban/inspectionKanbanColumns';

export type InspectionView = 'list' | 'kanban';
export type InspectionDateField = 'created_at' | 'finished_at';
export type InspectionSortDirection = 'asc' | 'desc';
export type InspectionSortField =
  | 'code' | 'vehicle_label' | 'customer_name' | 'template_name'
  | 'checklist_type_name' | 'status' | 'due_finish_at' | 'progress'
  | 'schedule_frequency' | 'origin' | 'assignee_mode' | 'assignee_name'
  | 'created_at' | 'finished_at';

export interface InspectionListRow {
  id: string;
  code: number;
  template_name: string;
  version_number: number;
  status: string;
  deadline_status: string;
  origin: string;
  executor_name: string | null;
  executor_avatar: string | null;
  due_finish_at: string | null;
  created_at: string;
  finished_at: string | null;
  fraud_alerts: number;
  items_total: number;
  items_answered: number;
  vehicle_plate: string | null;
  vehicle_label: string | null;
  // ID do veículo no vínculo da vistoria (congelado na criação), para abrir o modal do veículo.
  vehicle_id: string | null;
  customer_id: string | null;
  contact_id: string | null;
  customer_name: string | null;
  customer_avatar: string | null;
  // Frequência da periodicidade de origem (daily/weekly/monthly); null quando manual.
  schedule_frequency?: string | null;
  /** Tipo do template e modo configurado para atribuição da vistoria. */
  checklist_type_id: string | null;
  checklist_type_name: string | null;
  assignee_mode: 'user' | 'queue' | 'owner' | 'provider' | null;
  assignee_name: string | null;
  assignee_avatar: string | null;
  queue_name: string | null;
  reviewer_name: string | null;
  reviewer_avatar: string | null;
  total_records: number;
}

export interface InspectionFilters {
  search?: string;
  status?: string[] | null;
  deadlineStatus?: string | null;
  onlyFraud?: boolean;
  dateFrom?: string | null;
  dateTo?: string | null;
  dateField?: InspectionDateField;
  templateId?: string | null;
  customerId?: string | null;
  contactId?: string | null;
  typeId?: string | null;
  assigneeMode?: 'user' | 'queue' | 'owner' | 'provider' | null;
  assigneeUserId?: string | null;
  assigneeCustomerId?: string | null;
  assigneeContactId?: string | null;
  assigneeQueueId?: string | null;
  /** v1.7.0: recorte por origem da vistoria (manual ou periódica). */
  origin?: 'manual' | 'schedule' | null;
}

export type InspectionKpis = Record<
  'total' | 'pending' | 'in_progress' | 'awaiting_review' | 'correction_requested'
  | 'approved' | 'rejected' | 'cancelled' | 'with_fraud' | 'overdue',
  number
>;

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
const KANBAN_PAGE_SIZE = 5;
const logger = createLogger('ChecklistInstancesList');

interface InspectionCursor {
  created_at: string;
  id: string;
}

/** Monta os parâmetros comuns da RPC (limitando a busca a 100 caracteres). */
function buildParams(view: InspectionView, f: InspectionFilters, tenantId?: string) {
  return {
    p_view: view,
    p_search: f.search?.trim().slice(0, 100) || null,
    p_status_filter: f.status?.length ? f.status : null,
    p_template_id: f.templateId || null,
    p_deadline_status: f.deadlineStatus || null,
    p_only_fraud: !!f.onlyFraud,
    p_date_from: f.dateFrom || null,
    p_date_to: f.dateTo || null,
    p_customer_id: f.customerId || null,
    // Quando há cliente vinculado, o recorte é pelo cliente histórico inteiro;
    // o contato serve somente como fallback para proprietários ainda não convertidos.
    p_contact_id: f.customerId ? null : f.contactId || null,
    p_type_id: f.typeId || null,
    p_assignee_mode: f.assigneeMode || null,
    p_assignee_user_id: f.assigneeMode === 'user' ? f.assigneeUserId || null : null,
    p_assignee_customer_id: f.assigneeMode === 'owner' ? f.assigneeCustomerId || null : null,
    p_assignee_contact_id: f.assigneeMode === 'owner' && !f.assigneeCustomerId ? f.assigneeContactId || null : null,
    p_assignee_queue_id: f.assigneeMode === 'queue' ? f.assigneeQueueId || null : null,
    p_origin: f.origin || null,
    p_timezone: TZ,
    p_tenant_hint: tenantId ?? null,
  };
}

/** Lista paginada (página numérica). */
export function useInspectionsPage(
  view: InspectionView,
  filters: InspectionFilters,
  page: number,
  perPage = 25,
  sortField: InspectionSortField = 'created_at',
  sortDirection: InspectionSortDirection = 'desc',
) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['inspection-list', currentTenant?.id, view, filters, page, perPage, sortField, sortDirection],
    enabled: !!currentTenant?.id,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_checklist_instances_paginated' as never, {
        ...buildParams(view, filters, currentTenant?.id),
        p_page: page,
        p_per_page: perPage,
        p_sort_field: sortField,
        p_sort_direction: sortDirection,
      } as never);
      if (error) throw error;
      const rows = (data ?? []) as InspectionListRow[];
      return { rows, total: rows[0]?.total_records ?? 0 };
    },
  });
}

/** Coluna do Kanban com cursor composto estável (created_at + id). */
export function useInspectionsColumn(status: string, filters: InspectionFilters) {
  const { currentTenant } = useAuth();
  const query = useInfiniteQuery({
    queryKey: ['inspection-kanban-column', currentTenant?.id, status, filters],
    enabled: !!currentTenant?.id,
    initialPageParam: null as InspectionCursor | null,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await supabase.rpc('get_checklist_instances_paginated' as never, {
        ...buildParams('kanban', { ...filters, status: [status] }, currentTenant?.id),
        p_page: 1,
        p_per_page: KANBAN_PAGE_SIZE,
        p_cursor_created_at: pageParam?.created_at || null,
        p_cursor_id: pageParam?.id || null,
      } as never);
      if (error) {
        logger.error('Erro ao buscar coluna do Kanban de Vistorias', { status, error: error.message });
        throw error;
      }
      return (data ?? []) as InspectionListRow[];
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < KANBAN_PAGE_SIZE) return undefined;
      const lastItem = lastPage[lastPage.length - 1];
      return { created_at: lastItem.created_at, id: lastItem.id } as InspectionCursor;
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    // v1.6.0: polling de segurança (60s, só com aba visível), igual Propostas/Contratos.
    // Mantém os cartões anteriores enquanto um novo filtro carrega.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    placeholderData: (previous) => previous,
  });

  return {
    inspections: query.data?.pages.flat() ?? [],
    isLoading: query.isLoading,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    error: query.error,
  };
}

/** KPIs do topo. */
export function useInspectionKpis(view: InspectionView, filters: InspectionFilters, enabled = true) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['inspection-kanban-counts', currentTenant?.id, view, filters],
    enabled: !!currentTenant?.id && enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_checklist_instances_kpis' as never, {
        p_view: view,
        p_search: filters.search?.trim().slice(0, 100) || null,
        p_template_id: filters.templateId || null,
        p_deadline_status: filters.deadlineStatus || null,
        p_only_fraud: !!filters.onlyFraud,
        p_date_from: filters.dateFrom || null,
        p_date_to: filters.dateTo || null,
        p_date_field: filters.dateField || 'created_at',
        p_customer_id: filters.customerId || null,
        p_contact_id: filters.customerId ? null : filters.contactId || null,
        p_type_id: filters.typeId || null,
        p_assignee_mode: filters.assigneeMode || null,
        p_assignee_user_id: filters.assigneeMode === 'user' ? filters.assigneeUserId || null : null,
        p_assignee_customer_id: filters.assigneeMode === 'owner' ? filters.assigneeCustomerId || null : null,
        p_assignee_contact_id: filters.assigneeMode === 'owner' && !filters.assigneeCustomerId ? filters.assigneeContactId || null : null,
        p_assignee_queue_id: filters.assigneeMode === 'queue' ? filters.assigneeQueueId || null : null,
        p_timezone: TZ,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
      return (data ?? {}) as InspectionKpis;
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchInterval: view === 'kanban' ? 60_000 : false,
  });
}

/** Invalida colunas, contagens e listagem da Central de Vistorias. */
export function useInvalidateInspectionsKanban() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['inspection-kanban-column'] });
    qc.invalidateQueries({ queryKey: ['inspection-kanban-counts'] });
    qc.invalidateQueries({ queryKey: ['inspection-list'] });
  };
}

/** Realtime centralizado com movimentação otimista entre colunas. */
export function useInspectionsListRealtime(enabled = true) {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();
  useEffect(() => {
    if (!currentTenant?.id || !enabled) return;
    const tenantId = currentTenant.id;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    // v1.6.0: padrão Tarefas — com aba oculta apenas marca pendência; ao voltar, recarrega 1x.
    let pendingInvalidation = false;
    const flushAll = () => {
      pendingInvalidation = false;
      qc.invalidateQueries({ queryKey: ['inspection-kanban-column', tenantId] });
      qc.invalidateQueries({ queryKey: ['inspection-kanban-counts', tenantId] });
      qc.invalidateQueries({ queryKey: ['inspection-list', tenantId] });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && pendingInvalidation) flushAll();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const removeFromOtherColumns = (inspectionId: string, keepStatus: string | null) => {
      const queries = qc.getQueriesData<InfiniteData<InspectionListRow[]>>({
        queryKey: ['inspection-kanban-column', tenantId],
      });
      queries.forEach(([key, cached]) => {
        if (!cached) return;
        const status = key[2] as string | undefined;
        if (status === keepStatus) return;
        if (!cached.pages.some((page) => page.some((row) => row.id === inspectionId))) return;
        qc.setQueryData<InfiniteData<InspectionListRow[]>>(key, {
          ...cached,
          pages: cached.pages.map((page) => page.filter((row) => row.id !== inspectionId)),
        });
      });
    };

    const scheduleSharedInvalidation = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['inspection-kanban-counts', tenantId] });
        qc.invalidateQueries({ queryKey: ['inspection-list', tenantId] });
      }, 300);
    };

    const channel = supabase
      .channel(`checklist-instances-list-${tenantId}-${crypto.randomUUID()}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'checklist_instances', filter: `tenant_id=eq.${tenantId}`,
      }, (payload) => {
        const current = payload.new as { id?: string; status?: string } | null;
        const previous = payload.old as { id?: string; status?: string } | null;
        const inspectionId = current?.id || previous?.id;
        if (!inspectionId) return;
        if (document.visibilityState === 'hidden') { pendingInvalidation = true; return; }

        const nextStatus = resolveInspectionStatus(current?.status);
        removeFromOtherColumns(inspectionId, payload.eventType === 'DELETE' ? null : nextStatus);
        if (payload.eventType !== 'DELETE' && nextStatus) {
          qc.invalidateQueries({ queryKey: ['inspection-kanban-column', tenantId, nextStatus] });
        }
        scheduleSharedInvalidation();
      })
      .subscribe((status) => {
        logger.debug('Realtime Vistorias status', { status });
      });
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [currentTenant?.id, enabled, qc]);
}
